import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";

const Required = () => (
  <Typography component="span" sx={{ color: "error.main", ml: 0.25 }}>
    *
  </Typography>
);

const errorMessage = (err) => {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  return err?.message || "Unexpected error.";
};

const downloadBlob = async () => {
  const res = await api.get("/admin/employees/import/template", {
    responseType: "blob",
  });
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "employees_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function EmployeeImportPage() {
  const navigate = useNavigate();

  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("insert");

  const [previewBusy, setPreviewBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  const [report, setReport] = useState(null); // { mode, total_rows, valid_rows, error_rows, errors: [{row, column, message}] }
  const [runResult, setRunResult] = useState(null); // { inserted, updated, vacations_seeded }

  useEffect(() => {
    let active = true;
    setColumnsLoading(true);
    api
      .get("/admin/employees/import/columns")
      .then((res) => {
        if (!active) return;
        setColumns(res?.data?.columns || []);
      })
      .catch((err) => {
        if (!active) return;
        toast.error(errorMessage(err));
      })
      .finally(() => {
        if (active) setColumnsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const requiredColumns = useMemo(
    () => columns.filter((c) => c.required),
    [columns]
  );
  const optionalColumns = useMemo(
    () => columns.filter((c) => !c.required),
    [columns]
  );

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setReport(null);
    setRunResult(null);
  };

  const handleDownload = async () => {
    setDownloadBusy(true);
    try {
      await downloadBlob();
      toast.success("Template downloaded.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDownloadBusy(false);
    }
  };

  const submit = async (path, setBusy, onSuccess) => {
    if (!file) {
      toast.error("Pick a file first.");
      return;
    }
    setBusy(true);
    setRunResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const res = await api.post(path, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSuccess(res.data);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors && Array.isArray(data.errors)) {
        // Validation report from server (status 422)
        setReport({
          mode: data.mode,
          total_rows: data.total_rows,
          valid_rows: data.valid_rows,
          error_rows: data.error_rows,
          errors: data.errors,
        });
      }
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () =>
    submit("/admin/employees/import/preview", setPreviewBusy, (data) => {
      setReport(data);
      if ((data.error_rows || 0) === 0) {
        toast.success(`All ${data.valid_rows} rows valid.`);
      } else {
        toast.warning(
          `${data.error_rows} row(s) have errors. See list below.`
        );
      }
    });

  const handleRun = () =>
    submit("/admin/employees/import/run", setRunBusy, (data) => {
      setRunResult(data);
      setReport(null);
      toast.success(
        `Import done. Inserted ${data.inserted}, updated ${data.updated}, vacation rows seeded ${data.vacations_seeded}.`
      );
    });

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
          variant="text"
        >
          Back to Users
        </Button>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Import Employees
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        One unified template for every employee field. Upload the file, preview
        validation, then run the import. The system will derive the base role
        from the Actual Role and seed vacation balances based on the
        employee's gender and region using the configured policy.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={downloadBusy}
              onClick={handleDownload}
            >
              {downloadBusy ? "Preparing…" : "Download Template (.xlsx)"}
            </Button>

            <FormControl>
              <InputLabel id="import-mode-label" shrink>
                Mode
              </InputLabel>
              <RadioGroup
                row
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                aria-labelledby="import-mode-label"
                sx={{ ml: 1 }}
              >
                <FormControlLabel value="insert" control={<Radio />} label="Insert (new only)" />
                <FormControlLabel value="upsert" control={<Radio />} label="Upsert (update existing)" />
              </RadioGroup>
            </FormControl>
          </Stack>

          <Divider />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
              {file ? "Change File" : "Choose File"}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={onFileChange}
              />
            </Button>

            {file && (
              <Chip
                label={`${file.name} (${Math.round(file.size / 1024)} KB)`}
                onDelete={() => {
                  setFile(null);
                  setReport(null);
                  setRunResult(null);
                }}
                color="primary"
                variant="outlined"
              />
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={!file || previewBusy || runBusy}
              startIcon={previewBusy ? <CircularProgress size={16} /> : null}
            >
              {previewBusy ? "Validating…" : "Validate"}
            </Button>

            <Button
              variant="contained"
              onClick={handleRun}
              disabled={!file || runBusy || previewBusy}
              startIcon={runBusy ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            >
              {runBusy ? "Importing…" : "Run Import"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {runResult && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>Import complete</AlertTitle>
          Inserted: <strong>{runResult.inserted}</strong> · Updated:{" "}
          <strong>{runResult.updated}</strong> · Vacation rows seeded:{" "}
          <strong>{runResult.vacations_seeded}</strong>
        </Alert>
      )}

      {report && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Validation report
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <Chip label={`Total rows: ${report.total_rows}`} />
            <Chip label={`Valid: ${report.valid_rows}`} color="success" variant="outlined" />
            <Chip
              label={`Errors: ${report.error_rows}`}
              color={report.error_rows ? "error" : "default"}
              variant="outlined"
            />
            <Chip label={`Mode: ${report.mode}`} variant="outlined" />
          </Stack>

          {report.errors && report.errors.length > 0 ? (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.errors.map((e, idx) => (
                    <TableRow key={`${e.row}-${e.column}-${idx}`}>
                      <TableCell>{e.row}</TableCell>
                      <TableCell>
                        <code>{e.column}</code>
                      </TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="success">All rows passed validation.</Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Template columns
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Required fields are marked with{" "}
          <Typography component="span" sx={{ color: "error.main", fontWeight: 700 }}>
            *
          </Typography>
          . Dropdown values appear in the Excel template as data validations.
        </Typography>

        {columnsLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Required ({requiredColumns.length})
              </Typography>
              <ColumnList columns={requiredColumns} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Optional ({optionalColumns.length})
              </Typography>
              <ColumnList columns={optionalColumns} />
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
}

function ColumnList({ columns }) {
  return (
    <Stack spacing={1}>
      {columns.map((c) => {
        const sample =
          c.type === "enum" && Array.isArray(c.options) && c.options.length > 0
            ? c.options.slice(0, 6).join(", ") +
              (c.options.length > 6 ? `, … (+${c.options.length - 6})` : "")
            : null;
        return (
          <Paper
            key={c.key}
            variant="outlined"
            sx={{ p: 1.25, display: "flex", flexDirection: "column" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 700 }}>
                {c.label}
                {c.required ? <Required /> : null}
              </Typography>
              <Chip size="small" label={c.type} variant="outlined" />
            </Stack>
            {c.description && (
              <Typography variant="caption" color="text.secondary">
                {c.description}
              </Typography>
            )}
            {sample && (
              <Typography variant="caption" color="text.secondary">
                Values: {sample}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}
